import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  memo,
  useRef,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Form,
  Button,
  Card,
  Badge,
  Alert,
  Modal,
  Container,
  Row,
  Col,
  ProgressBar,
  Spinner,
} from "react-bootstrap";
import {
  IconUser,
  IconPalette,
  IconBuilding,
  IconFunction,
  IconFileText,
  IconChevronLeft,
  IconChevronRight,
  IconEye,
  IconDeviceFloppy,
  IconPhoto,
  IconHeart,
  IconWand,
  IconSparkles,
  IconMessageCircle,
  IconRocket,
  IconBook,
} from "@tabler/icons-react";
import Select from "react-select";
import Swal from "sweetalert2";

// React DND imports
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useLocation, useNavigate } from "react-router-dom";

// Importar services
import {
  // Agent services
  createAgent,
  updateAgent,
  copyAgentPrompt,

  // Image services
  listImages,

  // Draft services
  getCurrentDraft,
  saveDraft as saveDraftAPI,
  clearDraft,
  prepareDraftData,
  prepareAgentData,

  // FormData helpers
  createAgentFormData,
} from "../../../services/AgentsServices";

// AI services
import { generateAgentFieldContent } from "../../../services/AIServices";
import UserContext from "../../../contexts/UserContext";
import StepIdentity from "./components/step1";
import {
  AREA_ACTIVITY_OPTIONS,
  CONVERSATION_TEMPLATES,
  MICRO_INTERACTIONS,
  NEXT_STEP_OPIONS,
} from "./AgentsConstants";

// Import mobile CSS
import "./agentWizardMobile.css";
import StepLanguage from "./components/step2";
import StepBusiness from "./components/step3";
import StepConversation from "./components/step4";
import StepFunctions from "./components/step5";
import StepInteractions from "./components/step6";
import StepFinalPrompt from "./components/step7";
import NotificationsContext from "../../../contexts/NotificationsContext";
import { Hash } from "../../../helpers/Helpers";

// ✅ CORREÇÃO: Componente DnD que permite drag handle específico
export const DraggableStepItem = ({ step, index, moveStep, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "STEP_ITEM",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "STEP_ITEM",
    hover: (item) => {
      if (item.index !== index) {
        moveStep(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={drop}
      style={{
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {/* ✅ CORREÇÃO: Passar a ref do drag para o children */}
      {React.cloneElement(children, { dragRef: drag })}
    </div>
  );
};

const AIFillModal = ({ show, onHide, onGenerate, stepInfo, loading }) => {
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (show && stepInfo) {
      console.log(stepInfo);
      const defaultPrompt = `Crie um texto para a etapa "${
        stepInfo.label
      }" de um agente de IA para ${stepInfo.businessArea || "um negócio"}. 

Contexto da etapa: ${stepInfo.description || ""}

O texto deve ser:
- Natural e humanizado
- Direto ao ponto
- Apropriado para WhatsApp
- Sem formatação markdown

Retorne apenas o texto que o agente deve falar, sem explicações adicionais.`;

      setPrompt(defaultPrompt);
    }
  }, [show, stepInfo]);

  const handleGenerate = () => {
    onGenerate(prompt);
    setPrompt("");
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <IconWand className="me-2" size={20} />
          Preencher com IA
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <IconSparkles className="me-2" size={16} />A IA irá gerar o texto para
          a etapa "<strong>{stepInfo?.label}</strong>" baseado nas informações
          do seu agente.
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>Prompt para a IA</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Descreva o que você quer que a IA gere para esta etapa..."
          />
          <Form.Text className="text-muted">
            Você pode editar este prompt para obter resultados mais específicos
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={loading || !prompt.trim()}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Gerando...
            </>
          ) : (
            <>
              <IconWand className="me-2" size={16} />
              Gerar com IA
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const schemas = {
  step1: yup.object().shape({
    agentName: yup.string().required("Nome do agente é obrigatório"),
    agentJob: yup.string().required("Função do agente é obrigatório"),
    agentGender: yup.string().required("Gênero é obrigatório"),
    agentPersona: yup.array().min(1, "Selecione pelo menos uma personalidade"),
    agentArea: yup.string().required("Área de atuação é obrigatória"),
  }),
  step2: yup.object().shape({
    language: yup.string(),
    languageStyle: yup.array(),
    fundamentalRules: yup.array(),
    prohibitedWords: yup.string(),
    preferredWords: yup.string(),
  }),
  step3: yup.object().shape({
    businessInfo: yup
      .string()
      .required("Informações sobre o negócio são obrigatórias")
      .max(1200, "Máximo de 1200 caracteres"),
  }),
  step4: yup.object().shape({
    defaultFlowSteps: yup
      .array()
      .min(1, "Deve haver pelo menos uma etapa no fluxo padrão")
      .of(
        yup.object().shape({
          id: yup.string().required("ID da etapa é obrigatório"),
          label: yup.string().required("Nome da etapa é obrigatório"),
          key: yup.string().required("Chave da etapa é obrigatória"),
        })
      ),
    defaultFlow: yup.object(),
    specialFlows: yup.array(),
  }),
  step5: yup.object().shape({
    functionsEnabled: yup.boolean(),
  }),
  step6: yup.object().shape({
    interactions: yup.object(),
  }),
  step7: yup.object().shape({
    // Não há validações específicas para o step final
  }),
};

const AgentWizard = memo(
  ({
    imageLibrary = [],
    initialData = null,
    isUpgradeFromBasic = false,
    onBack = null,
  }) => {
    const location = useLocation();
    const data =
      initialData || (location?.state?.data ? location?.state?.data : null);
    const isEditing = location?.state?.isEditing
      ? location?.state?.isEditing
      : null;

    const { user } = useContext(UserContext);
    const { setNotify } = useContext(NotificationsContext);
    const [currentStep, setCurrentStep] = useState(1);
    // const [showPreview, setShowPreview] = useState(false);
    const [savedDraft, setSavedDraft] = useState(null);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [apiImageLibrary, setApiImageLibrary] = useState([]);
    const [loading, setLoading] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);
    const [agentId, setAgentId] = useState(null);
    const [getGeneratedSchemas, setGetGeneratedSchemas] = useState(null);

    // Novos estados para melhorias
    const [flowMode, setFlowMode] = useState("basic"); // "basic" ou "advanced"
    const [showAIModal, setShowAIModal] = useState(false);
    const [aiStepInfo, setAiStepInfo] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);

    // const [previewTrigger, setPreviewTrigger] = useState(0);

    // Form setup
    const form = useForm({
      defaultValues: {
        agentName: "",
        agentJob: "",
        agentGender: "",
        agentPersona: [],
        agentArea: "",
        agent_image: null,
        languageStyle: [],
        fundamentalRules: ["noMarkdown", "avoidFormal"],
        prohibitedWords: "",
        preferredWords: "",
        businessInfo: "",
        address: "",
        paymentMethods: [],

        // ✅ ADICIONAR: Etapas dinâmicas do fluxo padrão
        defaultFlowSteps: [
          {
            id: "step_" + Date.now(),
            key: "greeting",
            label: "Saudação",
            icon: "👋",
            description:
              "Como o agente vai cumprimentar cada pessoa que chegar",
            placeholder:
              "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
            order: 0,
          },
        ],

        defaultFlow: {
          greeting: "",
          presentation: "",
          needIdentification: "",
          direction: "",
          confirmation: "",
          upsell: "",
          availability: "",
          dataCollection: "",
          closing: "",
          greetingNextStep: "",
          presentationNextStep: "",
          needIdentificationNextStep: "",
          directionNextStep: "",
          confirmationNextStep: "",
          upsellNextStep: "",
          availabilityNextStep: "",
          dataCollectionNextStep: "",
          closingNextStep: "",
          customStepNames: {},
        },
        specialFlows: [],
        functionsEnabled: false,
        functionSchemas: {},
        interactions: {
          conectores: {
            enabled: false,
            value: MICRO_INTERACTIONS.conectores.defaultValue,
          },
          validacao: {
            enabled: false,
            value: MICRO_INTERACTIONS.validacao.defaultValue,
          },
          celebracao: {
            enabled: false,
            value: MICRO_INTERACTIONS.celebracao.defaultValue,
          },
          upsell: {
            enabled: false,
            value: MICRO_INTERACTIONS.upsell.defaultValue,
          },
          emojis: {
            enabled: false,
            value: MICRO_INTERACTIONS.emojis.defaultValue,
          },
          mantra: {
            enabled: false,
            value: MICRO_INTERACTIONS.mantra.defaultValue,
          },
        },
      },
      resolver:
        currentStep <= 7
          ? yupResolver(schemas[`step${currentStep}`] || yup.object())
          : undefined,
      mode: "onSubmit",
      reValidateMode: "onSubmit",
    });

    const {
      register,
      handleSubmit,
      formState: { errors },
      control,
      setValue,
      getValues,
      watch,
      reset,
      unregister,
    } = form;

    const specialFlowsRef = useRef([]);
    const prevSpecialFlowsRef = useRef("");
    const specialFlows = watch("specialFlows") || [];

    // Função para mapear dados da API para o formato do formulário
    const mapApiDataToForm = useCallback((apiData) => {
      if (!apiData) return {};

      console.log("🔍 DEBUG - Dados originais da API:", apiData);

      const formData = {};

      // ===================================================================
      // CAMPOS BÁSICOS DO AGENTE
      // ===================================================================

      // Nome do agente
      if (apiData.name || apiData.agent_name) {
        formData.agentName = apiData.name || apiData.agent_name;
      }

      //Função do agente
      if (apiData.agent_job) {
        formData.agentJob = apiData.agent_job;
      }

      // Gênero do agente
      if (apiData.agent_gender || apiData.gender) {
        formData.agentGender = apiData.agent_gender || apiData.gender;
      }

      // Área de atuação
      if (apiData.agent_area || apiData.area) {
        formData.agentArea = apiData.agent_area || apiData.area;
      }

      // Imagem do agente
      if (apiData.agent_image) {
        // let imageUrl = apiData.agent_image;

        formData.agent_image = apiData.agent_image;
      }

      // ===================================================================
      // CAMPOS DE ARRAY
      // ===================================================================

      // Personalidade do agente
      if (apiData.agent_persona || apiData.persona) {
        formData.agentPersona = Array.isArray(apiData.agent_persona)
          ? apiData.agent_persona
          : Array.isArray(apiData.persona)
          ? apiData.persona
          : [];
      }

      if (apiData.language && apiData.language !== "default") {
        formData.language = apiData.language;
      } else {
        formData.language = "default";
      }

      // Estilo de linguagem
      if (apiData.language_style || apiData.languageStyle) {
        formData.languageStyle = Array.isArray(apiData.language_style)
          ? apiData.language_style
          : Array.isArray(apiData.languageStyle)
          ? apiData.languageStyle
          : [];
      }

      // Regras fundamentais
      if (apiData.fundamental_rules || apiData.fundamentalRules) {
        formData.fundamentalRules = Array.isArray(apiData.fundamental_rules)
          ? apiData.fundamental_rules
          : Array.isArray(apiData.fundamentalRules)
          ? apiData.fundamentalRules
          : ["noMarkdown", "avoidFormal"];
      }

      // Métodos de pagamento
      if (apiData.payment_methods || apiData.paymentMethods) {
        formData.paymentMethods = Array.isArray(apiData.payment_methods)
          ? apiData.payment_methods
          : Array.isArray(apiData.paymentMethods)
          ? apiData.paymentMethods
          : [];
      }

      // ===================================================================
      // CAMPOS DE TEXTO
      // ===================================================================

      // Palavras proibidas
      if (apiData.prohibited_words || apiData.prohibitedWords) {
        formData.prohibitedWords =
          apiData.prohibited_words || apiData.prohibitedWords || "";
      }

      // Palavras preferidas
      if (apiData.preferred_words || apiData.preferredWords) {
        formData.preferredWords =
          apiData.preferred_words || apiData.preferredWords || "";
      }

      // Informações do negócio
      if (apiData.business_info || apiData.businessInfo) {
        formData.businessInfo =
          apiData.business_info || apiData.businessInfo || "";
      }

      // Endereço
      if (apiData.address) {
        formData.address = apiData.address || "";
      }

      // ===================================================================
      // FLUXO PADRÃO (DEFAULT FLOW) - ✅ CORRIGIDO para estrutura flat
      // ===================================================================

      let defaultFlowData = null;

      // ✅ CORREÇÃO: Verificar múltiplas estruturas possíveis
      if (
        apiData.default_flow ||
        apiData.default_flow_steps ||
        apiData.defaultFlowSteps
      ) {
        // Estrutura aninhada (formato antigo)
        defaultFlowData =
          apiData.default_flow ||
          apiData.default_flow_steps ||
          apiData.defaultFlowSteps;
        console.log(
          "🔍 DEBUG - Fluxo padrão encontrado em estrutura aninhada:",
          defaultFlowData
        );
      } else if (apiData.steps && Array.isArray(apiData.steps)) {
        // ✅ NOVO: Estrutura flat no nível raiz (formato atual)
        defaultFlowData = { ...apiData }; // Copiar tudo da raiz
        console.log(
          "🔍 DEBUG - Fluxo padrão encontrado em estrutura flat (raiz):",
          defaultFlowData
        );
      }

      if (defaultFlowData) {
        if (Array.isArray(defaultFlowData)) {
          // Se vier como array, usar como steps
          formData.defaultFlowSteps = defaultFlowData;
          formData.defaultFlow = {
            greeting: "",
            presentation: "",
            needIdentification: "",
            direction: "",
            confirmation: "",
            upsell: "",
            availability: "",
            dataCollection: "",
            closing: "",
            greetingNextStep: "",
            presentationNextStep: "",
            needIdentificationNextStep: "",
            directionNextStep: "",
            confirmationNextStep: "",
            upsellNextStep: "",
            availabilityNextStep: "",
            dataCollectionNextStep: "",
            closingNextStep: "",
            customStepNames: {},
          };
        } else {
          // ✅ CORREÇÃO: Se vier como objeto, extrair steps e dados corretamente

          // Extrair steps (metadados das etapas)
          formData.defaultFlowSteps = defaultFlowData.steps || [
            {
              id: "step_" + Date.now(),
              key: "greeting",
              label: "Saudação",
              icon: "👋",
              description:
                "Como o agente vai cumprimentar cada pessoa que chegar",
              placeholder:
                "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
              order: 0,
            },
          ];

          // ✅ CORREÇÃO: Filtrar e copiar apenas campos relevantes para defaultFlow
          const relevantFields = {};

          // Copiar campos de conteúdo das etapas (baseado nas chaves dos steps)
          if (defaultFlowData.steps && Array.isArray(defaultFlowData.steps)) {
            defaultFlowData.steps.forEach((step) => {
              if (step.key) {
                // Copiar conteúdo da etapa
                if (defaultFlowData[step.key] !== undefined) {
                  relevantFields[step.key] = defaultFlowData[step.key];
                }

                // Copiar próxima etapa
                const nextStepKey = `${step.key}NextStep`;
                if (defaultFlowData[nextStepKey] !== undefined) {
                  relevantFields[nextStepKey] = defaultFlowData[nextStepKey];
                }
              }
            });
          }

          // Copiar campos padrão conhecidos
          const knownDefaultFlowFields = [
            "greeting",
            "presentation",
            "needIdentification",
            "direction",
            "confirmation",
            "upsell",
            "availability",
            "dataCollection",
            "closing",
            "greetingNextStep",
            "presentationNextStep",
            "needIdentificationNextStep",
            "directionNextStep",
            "confirmationNextStep",
            "upsellNextStep",
            "availabilityNextStep",
            "dataCollectionNextStep",
            "closingNextStep",
            "customStepNames",
          ];

          knownDefaultFlowFields.forEach((field) => {
            if (defaultFlowData[field] !== undefined) {
              relevantFields[field] = defaultFlowData[field];
            }
          });

          // ✅ NOVO: Log para debug
          console.log("🔍 DEBUG - Campos mapeados para defaultFlow:", {
            originalKeys: Object.keys(defaultFlowData),
            mappedKeys: Object.keys(relevantFields),
            steps: formData.defaultFlowSteps,
            relevantFields: relevantFields,
          });

          formData.defaultFlow = {
            // Valores padrão
            greeting: "",
            presentation: "",
            needIdentification: "",
            direction: "",
            confirmation: "",
            upsell: "",
            availability: "",
            dataCollection: "",
            closing: "",
            greetingNextStep: "",
            presentationNextStep: "",
            needIdentificationNextStep: "",
            directionNextStep: "",
            confirmationNextStep: "",
            upsellNextStep: "",
            availabilityNextStep: "",
            dataCollectionNextStep: "",
            closingNextStep: "",
            customStepNames: {},

            // ✅ SOBRESCREVER com dados da API
            ...relevantFields,
          };
        }
      } else {
        // Valores padrão se não houver dados
        formData.defaultFlowSteps = [
          {
            id: "step_" + Date.now(),
            key: "greeting",
            label: "Saudação",
            icon: "👋",
            description:
              "Como o agente vai cumprimentar cada pessoa que chegar",
            placeholder:
              "Exemplo: Oi! Tudo bem? Eu sou a Maria, como posso te ajudar hoje? 😊",
            order: 0,
          },
        ];

        formData.defaultFlow = {
          greeting: "",
          presentation: "",
          needIdentification: "",
          direction: "",
          confirmation: "",
          upsell: "",
          availability: "",
          dataCollection: "",
          closing: "",
          greetingNextStep: "",
          presentationNextStep: "",
          needIdentificationNextStep: "",
          directionNextStep: "",
          confirmationNextStep: "",
          upsellNextStep: "",
          availabilityNextStep: "",
          dataCollectionNextStep: "",
          closingNextStep: "",
          customStepNames: {},
        };
      }

      // ===================================================================
      // VERIFICAÇÃO ADICIONAL: Log completo para debug
      // ===================================================================

      console.log("🔍 DEBUG - Resultado final do mapeamento defaultFlow:", {
        defaultFlowSteps: formData.defaultFlowSteps,
        defaultFlow: formData.defaultFlow,
        originalApiData: {
          hasDefaultFlow: !!(
            apiData.default_flow ||
            apiData.default_flow_steps ||
            apiData.defaultFlowSteps
          ),
          hasStepsInRoot: !!apiData.steps,
          rootKeys: Object.keys(apiData),
          stepsFromRoot: apiData.steps,
        },
      });

      // ===================================================================
      // TESTE: Verificar se os campos específicos foram mapeados
      // ===================================================================

      if (apiData.steps && Array.isArray(apiData.steps)) {
        console.log("🔍 TESTE - Verificando mapeamento específico dos steps:");

        apiData.steps.forEach((step, index) => {
          if (step.key) {
            const contentKey = step.key;
            const nextStepKey = `${step.key}NextStep`;

            console.log(`🔍 Step ${index} (${step.key}):`, {
              stepData: step,
              contentFromApi: apiData[contentKey],
              nextStepFromApi: apiData[nextStepKey],
              contentInFormData: formData.defaultFlow[contentKey],
              nextStepInFormData: formData.defaultFlow[nextStepKey],
              wasMapped: !!(
                formData.defaultFlow[contentKey] ||
                formData.defaultFlow[nextStepKey]
              ),
            });
          }
        });
      }

      // ===================================================================
      // FLUXOS ESPECIAIS - ✅ MANTIDO (já funcionando corretamente)
      // ===================================================================

      if (apiData.special_flows || apiData.specialFlows) {
        const specialFlowsData = apiData.special_flows || apiData.specialFlows;

        if (Array.isArray(specialFlowsData)) {
          formData.specialFlows = specialFlowsData.map((flow, flowIndex) => {
            // ✅ SIMPLES: Copiar tudo diretamente, sem conversões!
            const mappedFlow = {
              name: flow.name || "",
              triggers: flow.triggers || "",
              steps: flow.steps || [],

              // ✅ COPIA TODOS os outros campos diretamente
              // Não precisa converter nada porque já estão com step.key!
              ...Object.keys(flow)
                .filter((key) => !["name", "triggers", "steps"].includes(key))
                .reduce((acc, key) => {
                  acc[key] = flow[key];
                  return acc;
                }, {}),
            };

            return mappedFlow;
          });
        } else {
          formData.specialFlows = [];
        }
      } else {
        formData.specialFlows = [];
      }

      // ===================================================================
      // FUNÇÕES E SCHEMAS
      // ===================================================================

      // Funções habilitadas
      if (
        apiData.functions_enabled !== undefined ||
        apiData.functionsEnabled !== undefined
      ) {
        formData.functionsEnabled =
          apiData.functions_enabled || apiData.functionsEnabled || false;
      }

      // Schemas das funções
      if (
        apiData.function_responses ||
        apiData.functionResponses ||
        apiData.function_schemas ||
        apiData.functionSchemas
      ) {
        const functionsData =
          apiData.function_responses ||
          apiData.functionResponses ||
          apiData.function_schemas ||
          apiData.functionSchemas ||
          {};

        formData.functionSchemas = {};

        Object.keys(functionsData).forEach((functionId) => {
          const functionConfig = functionsData[functionId];

          if (functionConfig && typeof functionConfig === "object") {
            if (functionConfig.enabled) {
              // ✅ CORREÇÃO: Converter valores ["on"] para boolean
              const convertBooleanValue = (value) => {
                if (Array.isArray(value) && value[0] === "on") return true;
                if (value === "on") return true;
                return Boolean(value);
              };

              // Processar fields corrigindo valores ["on"]
              const processedFields = {};
              if (
                functionConfig.fields &&
                typeof functionConfig.fields === "object"
              ) {
                Object.keys(functionConfig.fields).forEach((fieldId) => {
                  const field = functionConfig.fields[fieldId];
                  processedFields[fieldId] = {
                    enabled: convertBooleanValue(field?.enabled),
                    required: convertBooleanValue(field?.required),
                  };
                });
              }

              // ✅ SÓ mapear dados completos se a função estiver habilitada
              formData.functionSchemas[functionId] = {
                enabled: true,
                useDefault:
                  functionConfig.useDefault !== undefined
                    ? functionConfig.useDefault
                    : true,
                customResponse: functionConfig.customResponse || "",
                responseMode: functionConfig.responseMode || "ai_full",
                registerClientData: convertBooleanValue(
                  functionConfig.registerClientData
                ),
                registerContactData: convertBooleanValue(
                  functionConfig.registerContactData
                ),
                fields: processedFields,
                customFields: functionConfig.customFields || [],

                // ✅ NOVO: Campos específicos para função "atendimento_humano"
                sendCustomResponse: convertBooleanValue(
                  functionConfig.sendCustomResponse
                ),
                operatorMessageType: functionConfig.operatorMessageType || "default",
                operatorMessage: functionConfig.operatorMessage || "",
                generateAIMessage: convertBooleanValue(
                  functionConfig.generateAIMessage
                ),
              };
            } else {
              // ✅ Para funções desabilitadas, usar estrutura limpa
              formData.functionSchemas[functionId] = {
                enabled: false,
                useDefault: true,
                customResponse: "",
                responseMode: "ai_full",
                registerClientData: false,
                registerContactData: false,
                fields: {},
                customFields: [],
                // Campos específicos para função "atendimento_humano"
                sendCustomResponse: false,
                operatorMessageType: "default",
                operatorMessage: "",
                generateAIMessage: false,
              };
            }
          }
        });
      }

      // ===================================================================
      // MICRO-INTERAÇÕES
      // ===================================================================

      if (apiData.interactions) {
        formData.interactions = {
          conectores: {
            enabled: apiData.interactions.conectores?.enabled || false,
            value:
              apiData.interactions.conectores?.value ||
              MICRO_INTERACTIONS.conectores.defaultValue,
          },
          validacao: {
            enabled: apiData.interactions.validacao?.enabled || false,
            value:
              apiData.interactions.validacao?.value ||
              MICRO_INTERACTIONS.validacao.defaultValue,
          },
          celebracao: {
            enabled: apiData.interactions.celebracao?.enabled || false,
            value:
              apiData.interactions.celebracao?.value ||
              MICRO_INTERACTIONS.celebracao.defaultValue,
          },
          upsell: {
            enabled: apiData.interactions.upsell?.enabled || false,
            value:
              apiData.interactions.upsell?.value ||
              MICRO_INTERACTIONS.upsell.defaultValue,
          },
          emojis: {
            enabled: apiData.interactions.emojis?.enabled || false,
            value:
              apiData.interactions.emojis?.value ||
              MICRO_INTERACTIONS.emojis.defaultValue,
          },
          mantra: {
            enabled: apiData.interactions.mantra?.enabled || false,
            value:
              apiData.interactions.mantra?.value ||
              MICRO_INTERACTIONS.mantra.defaultValue,
          },
        };
      } else {
        // Valores padrão para interações
        formData.interactions = {
          conectores: {
            enabled: false,
            value: MICRO_INTERACTIONS.conectores.defaultValue,
          },
          validacao: {
            enabled: false,
            value: MICRO_INTERACTIONS.validacao.defaultValue,
          },
          celebracao: {
            enabled: false,
            value: MICRO_INTERACTIONS.celebracao.defaultValue,
          },
          upsell: {
            enabled: false,
            value: MICRO_INTERACTIONS.upsell.defaultValue,
          },
          emojis: {
            enabled: false,
            value: MICRO_INTERACTIONS.emojis.defaultValue,
          },
          mantra: {
            enabled: false,
            value: MICRO_INTERACTIONS.mantra.defaultValue,
          },
        };
      }

      // ===================================================================
      // ARQUIVOS DE TREINAMENTO (RAG) - ✅ NOVO
      // ===================================================================

      if (apiData.training_files || apiData.trainingFiles) {
        const trainingFilesData =
          apiData.training_files || apiData.trainingFiles;

        if (Array.isArray(trainingFilesData)) {
          formData.trainingFiles = trainingFilesData.map((file) => ({
            id: file.id,
            name: file.name || file.filename,
            size: file.size,
            type: file.type || file.content_type,
            uploadedAt: file.uploaded_at || file.created_at,
            url: file.url,
            status: file.status || "processed",
          }));
        } else {
          formData.trainingFiles = [];
        }
      } else {
        formData.trainingFiles = [];
      }

      // ===================================================================
      // NOVOS CAMPOS - ✅ ADICIONADOS
      // ===================================================================

      // Campo simple_agent
      if (apiData.simple_agent !== undefined) {
        formData.simple_agent = apiData.simple_agent;
      } else {
        formData.simple_agent = false; // Padrão para agentes avançados
      }

      // Campo is_published
      if (apiData.is_published !== undefined) {
        formData.is_published = apiData.is_published;
      } else {
        formData.is_published = false; // Padrão não publicado
      }

      // Campo model (se não estiver sendo mapeado em outro lugar)
      if (apiData.model) {
        formData.model = apiData.model;
      } else {
        formData.model = "gpt-4.1-mini"; // Padrão
      }

      // Campo is_active (se não estiver sendo mapeado em outro lugar)
      if (apiData.is_active !== undefined) {
        formData.is_active = apiData.is_active;
      } else {
        formData.is_active = 0; // Padrão inativo
      }

      // ✅ NOVO: Campos Vision (análise de imagens e PDFs)
      if (apiData.vision_enabled !== undefined) {
        formData.vision_enabled = apiData.vision_enabled;
      } else {
        formData.vision_enabled = false; // Padrão desabilitado
      }

      if (apiData.vision_prompt !== undefined) {
        formData.vision_prompt = apiData.vision_prompt;
      } else {
        formData.vision_prompt = ""; // Padrão vazio (usará prompt automático baseado na área)
      }

      if (apiData.vision_detail) {
        formData.vision_detail = apiData.vision_detail;
      } else {
        formData.vision_detail = "high"; // Padrão alta qualidade
      }

      return formData;
    }, []);

    useEffect(() => {
      if (isEditing && data) {
        setDataLoading(true);

        try {
          if (data.id) {
            setAgentId(data.id);
          }

          const mappedData = mapApiDataToForm(data);
          reset(mappedData);

          if (data.images && Array.isArray(data.images)) {
            setUploadedImages(data.images);
          }

          if (data.currentStep) {
            setCurrentStep(data.currentStep);
          }

          if (setNotify) {
            setNotify({
              type: "success",
              title: "Dados carregados",
              message: "Dados do agente carregados com sucesso para edição.",
              timer: 3000,
            });
          }
        } catch (error) {
          console.error("Erro ao carregar dados do agente:", error);
          if (setNotify) {
            setNotify({
              type: "error",
              title: "Erro ao carregar dados",
              message:
                "Não foi possível carregar os dados do agente para edição.",
            });
          }
        } finally {
          setDataLoading(false);
        }
      }
    }, [isEditing, data?.id]);

    // Reset form when switching from edit to create mode
    useEffect(() => {
      if (!isEditing && !data) {
        setAgentId(null);
        setUploadedImages([]);
        setCurrentStep(1);
        reset();
      }
    }, [isEditing, data, reset]);

    // Create image options for Select combinando API e prop (otimizado)
    const allImages = [...imageLibrary, ...apiImageLibrary, ...uploadedImages];
    const imageOptions = React.useMemo(
      () =>
        allImages.map((img) => ({
          value: `${img.url}`,
          label: img.name,
          image: `${img.url}`,
        })),
      [imageLibrary, apiImageLibrary, uploadedImages]
    );

    // Custom image select component (otimizado)
    const ImageSelect = React.memo(
      ({ value, onChange, placeholder, fieldName, onImageInsert }) => {
        const formatOptionLabel = useCallback(
          ({ label, image }) => (
            <div style={{ display: "flex", alignItems: "center" }}>
              <img
                src={image}
                alt={label}
                style={{
                  width: 30,
                  height: 30,
                  marginRight: 10,
                  objectFit: "cover",
                }}
              />
              {label}
            </div>
          ),
          []
        );

        const handleChange = useCallback(
          (selected) => {
            if (selected) {
              const imageUrl = selected.value;

              // Se houver uma função específica para inserir imagem (do dinamicFlowStep), usar ela
              if (onImageInsert && typeof onImageInsert === "function") {
                onImageInsert(imageUrl);
              } else if (onChange) {
                // Fallback para componentes que usam onChange diretamente
                const currentValue = value || "";
                const separator =
                  currentValue && currentValue.trim() !== "" ? "\n\n" : "";
                const newValue = currentValue + separator + imageUrl;
                onChange(newValue);
              }
            }
          },
          [value, onChange, onImageInsert]
        );

        return (
          <>
            <div
              className="text-muted form-text mb-3 ml-2"
              style={{ marginTop: -10, marginLeft: 10 }}
            >
              Use [[break]] para enviar mensagem por partes
            </div>
            <div className="mb-2">
              <Form.Label>
                <IconPhoto className="me-1" size={16} />
                Inserir imagem
              </Form.Label>
              <Select
                options={imageOptions}
                placeholder={placeholder}
                formatOptionLabel={formatOptionLabel}
                onChange={handleChange}
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "35px",
                    fontSize: "14px",
                  }),
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
          </>
        );
      }
    );

    // Load image library from API
    const loadImageLibrary = useCallback(async () => {
      try {
        const response = await listImages();
        setApiImageLibrary(response.data.results);
      } catch (error) {
        console.error("Erro ao carregar biblioteca de imagens:", error);
      }
    }, []);

    // Load draft from API (only if not editing)
    const loadDraft = useCallback(async () => {
      if (isEditing) return;

      try {
        setDraftLoading(true);
        const response = await getCurrentDraft();

        if (response.data) {
          setSavedDraft(response.data);

          Swal.fire({
            title: "Rascunho encontrado!",
            text: "Deseja continuar de onde parou?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Sim, continuar",
            cancelButtonText: "Começar do zero",
          }).then(async (result) => {
            if (result.isConfirmed) {
              // Carregar dados do rascunho
              Object.keys(response.data.data).forEach((key) => {
                setValue(key, response.data.data[key]);
              });
              setCurrentStep(response.data.currentStep || 1);
              if (response.data.images) {
                setUploadedImages(response.data.images);
              }
            } else {
              // Limpar rascunho do servidor

              try {
                await clearDraft(response.data.data.id);
                setSavedDraft(null);
              } catch (error) {
                console.error("Erro ao limpar rascunho:", error);
              }
            }
          });
        }
      } catch (error) {
        // Não há rascunho - comportamento normal
        console.log("Nenhum rascunho encontrado");
      } finally {
        setDraftLoading(false);
      }
    }, [setValue, isEditing]);

    // Load data on mount
    useEffect(() => {
      loadImageLibrary();
      loadDraft();
    }, [loadImageLibrary, loadDraft]);

    // Save draft function (disabled when editing)
    const handleSaveDraft = useCallback(async () => {
      if (isEditing) {
        if (setNotify) {
          setNotify({
            type: "warning",
            title: "Não disponível",
            message: "Salvar rascunho não está disponível no modo de edição.",
          });
        }
        return;
      }

      try {
        setDraftLoading(true);
        const formData = getValues();
        const draftData = prepareDraftData(
          currentStep,
          formData,
          uploadedImages
        );

        const response = await saveDraftAPI(draftData);
        setSavedDraft(response.data.draft);

        Swal.fire({
          icon: "success",
          title: "Rascunho salvo!",
          text: "Você pode continuar depois de onde parou.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Erro ao salvar rascunho:", error);
        Swal.fire({
          icon: "error",
          title: "Erro ao salvar rascunho",
          text: "Não foi possível salvar o rascunho. Tente novamente.",
        });
      } finally {
        setDraftLoading(false);
      }
    }, [currentStep, getValues, uploadedImages, isEditing, setNotify]);

    // AI Fill handler
    const handleAIFill = (stepKey, stepInfo) => {
      const currentData = getValues();
      setAiStepInfo({
        ...stepInfo,
        stepKey,
        agentName: currentData.agentName,
        businessArea: AREA_ACTIVITY_OPTIONS.find(
          (opt) => opt.value === currentData.agentArea
        )?.label,
        personality: currentData.agentPersona?.join(", "),
      });
      setShowAIModal(true);
    };

    const handleAIGenerate = async (prompt) => {
      setAiLoading(true);
      try {
        console.log("🤖 Iniciando geração de conteúdo com IA:", {
          stepKey: aiStepInfo?.stepKey,
          prompt: prompt.substring(0, 100) + "...",
          agentInfo: {
            name: aiStepInfo?.agentName,
            businessArea: aiStepInfo?.businessArea,
            personality: aiStepInfo?.personality,
          },
        });

        // Chamada real para a API de IA
        const response = await generateAgentFieldContent({
          prompt,
          agentName: aiStepInfo?.agentName,
          businessArea: aiStepInfo?.businessArea,
          personality: aiStepInfo?.personality,
          stepType: aiStepInfo?.key,
          stepDescription: aiStepInfo?.description,
        });

        if (response.success && response.response) {
          // Atualizar o campo correspondente
          setValue(aiStepInfo.stepKey, response.response);

          setShowAIModal(false);
          setAiStepInfo(null);

          Swal.fire({
            icon: "success",
            title: "Conteúdo gerado com IA!",
            text: `Texto criado usando ${response.model_used || "IA"} (${
              response.tokens_used || 0
            } tokens)`,
            timer: 3000,
            showConfirmButton: false,
          });
        } else {
          console.error("❌ Erro na resposta da IA:", response);
          throw new Error(response.error || "Resposta inválida da IA");
        }
      } catch (error) {
        console.error("❌ Erro ao gerar conteúdo:", error);

        // Tratamento de erros específicos
        let errorTitle = "Erro ao gerar conteúdo";
        let errorMessage =
          "Não foi possível gerar o conteúdo com IA. Tente novamente.";

        if (error.response?.data) {
          // Erro da API
          const apiError = error.response.data;
          errorMessage = apiError.error || errorMessage;

          if (apiError.errorType === "VALIDATION_ERROR") {
            errorTitle = "Dados inválidos";
            errorMessage = "Por favor, verifique o prompt e tente novamente.";
          } else if (apiError.errorType === "AI_API_ERROR") {
            errorTitle = "Serviço temporariamente indisponível";
            errorMessage =
              "O serviço de IA está temporariamente indisponível. Tente novamente em alguns minutos.";
          } else if (apiError.errorType === "CONNECTION_ERROR") {
            errorTitle = "Problema de conexão";
            errorMessage =
              "Verifique sua conexão de internet e tente novamente.";
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire({
          icon: "error",
          title: errorTitle,
          text: errorMessage,
          confirmButtonText: "Tentar novamente",
          showCancelButton: true,
          cancelButtonText: "Fechar",
        }).then((result) => {
          if (result.isConfirmed) {
            // Permite que o usuário tente novamente sem fechar o modal
            return;
          } else {
            // Fechar o modal se o usuário cancelar
            setShowAIModal(false);
            setAiStepInfo(null);
          }
        });
      } finally {
        setAiLoading(false);
      }
    };

    // Apply template
    const applyTemplate = (templateKey) => {
      const template = CONVERSATION_TEMPLATES[templateKey];
      if (!template) return;

      Swal.fire({
        title: `Aplicar ${template.name}?`,
        text: "Isso substituirá o conteúdo atual do fluxo padrão.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sim, aplicar",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          // Aplicar template no fluxo padrão
          const newDefaultFlow = { ...getValues("defaultFlow") };

          template.steps.forEach((step) => {
            newDefaultFlow[step.key] = "";
            if (!newDefaultFlow.customStepNames) {
              newDefaultFlow.customStepNames = {};
            }
            newDefaultFlow.customStepNames[step.key] = step.label;
          });

          setValue("defaultFlow", newDefaultFlow);

          Swal.fire({
            icon: "success",
            title: "Template aplicado!",
            text: "O template foi aplicado ao fluxo padrão.",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      });
    };

    // Navigation functions
    const nextStep = async () => {
      const currentSchema = schemas[`step${currentStep}`];
      if (currentSchema) {
        const currentData = getValues();
        try {
          await currentSchema.validate(currentData, { abortEarly: false });
          setCurrentStep((prev) => Math.min(prev + 1, 7));
        } catch (validationError) {
          console.log(validationError);
          Swal.fire({
            icon: "error",
            title: "Campos obrigatórios",
            text: "Por favor, preencha todos os campos obrigatórios antes de continuar.",
          });
        }
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, 7));
      }
    };

    const prevStep = () => {
      setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    // Add special flow (copying default flow structure)
    const addSpecialFlow = useCallback(() => {
      const currentFlows = watch("specialFlows") || [];

      const newFlow = {
        name: "",
        triggers: "",
        steps: [
          {
            id: "special_step_" + Date.now() + "_" + currentFlows.length,
            key: "greeting",
            label: "Saudação",
            icon: "👋",
            description:
              "Como o agente vai cumprimentar cada pessoa que chegar",
            order: 0,
          },
        ],
        // ✅ CORREÇÃO: Remover campos que serão gerenciados pelo formulário
      };

      setValue("specialFlows", [...currentFlows, newFlow], {
        shouldValidate: false,
        shouldDirty: false,
      });
    }, [setValue]);

    // Remove special flow
    const removeSpecialFlow = useCallback(
      (index) => {
        Swal.fire({
          title: "Excluir fluxo?",
          text: "Esta ação não poderá ser desfeita.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, excluir",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#dc3545",
        }).then((result) => {
          if (result.isConfirmed) {
            const currentFlows = watch("specialFlows") || [];
            const newFlows = currentFlows.filter((_, i) => i !== index);
            setValue("specialFlows", newFlows, {
              shouldValidate: false,
              shouldDirty: false,
            });
          }
        });
      },
      [setValue]
    );

    const memoizedSpecialFlows = React.useMemo(() => {
      // Verificar se há mudanças reais nos dados
      const currentSpecialFlowsStr = JSON.stringify(specialFlows);

      if (prevSpecialFlowsRef.current === currentSpecialFlowsStr) {
        return specialFlowsRef.current;
      }

      prevSpecialFlowsRef.current = currentSpecialFlowsStr;

      if (!Array.isArray(specialFlows)) {
        specialFlowsRef.current = [];
        return [];
      }

      // ✅ CORREÇÃO: Processar sem getValues() que causa instabilidade
      const processedFlows = specialFlows.map((flow, index) => ({
        ...flow,
        index,
        // ✅ CORREÇÃO: Remover formData que causava problemas
        // A estrutura será acessada diretamente via watch() quando necessário
      }));

      specialFlowsRef.current = processedFlows;
      return processedFlows;
    }, [specialFlows]);

    // Move flow for drag and drop
    const moveFlow = useCallback(
      (dragIndex, hoverIndex) => {
        // Obter dados atuais diretamente
        const currentFlows = watch("specialFlows") || [];
        const newFlows = [...currentFlows];
        const draggedFlow = newFlows[dragIndex];

        newFlows.splice(dragIndex, 1);
        newFlows.splice(hoverIndex, 0, draggedFlow);

        setValue("specialFlows", newFlows, {
          shouldValidate: false,
          shouldDirty: false,
        });
      },
      [setValue] // ✅ CORREÇÃO: Apenas setValue como dependência estável
    );

    const onSubmit = async (data) => {
      try {
        setLoading(true);

        console.log("🔍 Dados do formulário no onSubmit:", data);

        const defaultFlowWithContent = {
          steps: data.defaultFlowSteps || [], // Metadados das etapas
          // ✅ CORREÇÃO: Coletar conteúdo das etapas do fluxo padrão
          ...Object.keys(data.defaultFlow || {}).reduce((acc, key) => {
            if (data.defaultFlow[key]) {
              acc[key] = data.defaultFlow[key];
            }
            return acc;
          }, {}),
        };

        // ✅ CORREÇÃO CRÍTICA: Preparar specialFlows com acesso correto aos dados
        const specialFlowsWithContent = [];

        if (data.specialFlows && Array.isArray(data.specialFlows)) {
          data.specialFlows.forEach((flow, flowIndex) => {
            console.log(`🔍 Processando fluxo especial ${flowIndex}:`, flow);

            const flowName = flow.name || "";
            const flowTriggers = flow.triggers || "";
            const flowSteps = flow.steps || [];

            // ✅ CORREÇÃO: Coletar conteúdo das etapas usando acesso DIRETO ao flow
            const flowContentData = {};

            if (Array.isArray(flowSteps)) {
              flowSteps.forEach((step) => {
                if (step && step.key) {
                  // ✅ CORREÇÃO CRÍTICA: Acessar dados diretamente do flow, não de uma estrutura aninhada
                  const stepContent = flow[step.key];
                  const nextStepContent = flow[`${step.key}NextStep`];

                  console.log(`🔍 Coletando conteúdo da etapa ${step.key}:`, {
                    stepId: step.id,
                    stepKey: step.key,
                    content: stepContent,
                    nextStep: nextStepContent,
                    hasContent: stepContent && stepContent.trim() !== "",
                  });

                  if (stepContent && stepContent.trim() !== "") {
                    // ✅ CORREÇÃO: Usar step.key como chave para o conteúdo
                    flowContentData[step.key] = stepContent.trim();
                  }

                  // ✅ CORREÇÃO: Coletar nextStep se existir
                  if (nextStepContent && nextStepContent.trim() !== "") {
                    flowContentData[`${step.key}NextStep`] =
                      nextStepContent.trim();
                  }
                }
              });
            }

            // ✅ CORREÇÃO: Só adicionar fluxo se tiver nome E conteúdo
            const hasContent = Object.keys(flowContentData).length > 0;
            const hasValidName = flowName && flowName.trim() !== "";

            if (hasValidName && hasContent) {
              const specialFlowData = {
                name: flowName.trim(),
                triggers: flowTriggers.trim(),
                steps: flowSteps, // Metadados das etapas
                ...flowContentData, // Conteúdo das etapas
              };

              console.log(
                `🔍 ✅ Fluxo especial ${flowIndex} adicionado:`,
                specialFlowData
              );
              specialFlowsWithContent.push(specialFlowData);
            } else {
              console.log(
                `🔍 ❌ Fluxo especial ${flowIndex} ignorado - Nome válido: ${hasValidName}, Tem conteúdo: ${hasContent}`
              );
            
            }
          });
        }

        // ✅ NOVO: Separar agent_image para upload posterior
        const agentImageFile =
          data.agent_image instanceof File ? data.agent_image : null;

        // ✅ CORREÇÃO: Preparar dados do agente SEM agent_image para primeira requisição
        const rawAgentData = {
          // Mapear campos do formulário para o formato da API
          name: data.agentName,
          description: `${data.agentJob} ${data.agentPersona?.join(
            ", "
          )} para ${
            AREA_ACTIVITY_OPTIONS.find((opt) => opt.value === data.agentArea)
              ?.label || data.agentArea
          }`,
          agent_name: data.agentName,
          agent_job: data.agentJob,
          agent_gender: data.agentGender,
          agent_persona: data.agentPersona,
          agent_area: data.agentArea,
          language: data.language,
          language_style: data.languageStyle,
          fundamental_rules: data.fundamentalRules,
          prohibited_words: data.prohibitedWords,
          preferred_words: data.preferredWords,
          business_info: data.businessInfo,
          address: data.address,
          payment_methods: data.paymentMethods,

          // ✅ CORREÇÃO: Estrutura corrigida para fluxos
          default_flow: defaultFlowWithContent,
          special_flows: specialFlowsWithContent,

          functions_enabled: data.functionsEnabled,
          function_responses: data.functionsEnabled
            ? // ✅ FILTRAR: Só incluir funções habilitadas
              Object.keys(data.functionSchemas || {}).reduce(
                (acc, functionId) => {
                  const functionData = data.functionSchemas[functionId];
                  if (functionData && functionData.enabled === true) {
                    acc[functionId] = functionData;
                  }
                  return acc;
                },
                {}
              )
            : {},
          function_schemas: getGeneratedSchemas || {},

          // ✅ NOVO: Campos de Vision (análise de imagens e PDFs)
          vision_enabled: data.vision_enabled || false,
          vision_prompt: data.vision_prompt || null,
          vision_detail: data.vision_detail || "high",

          interactions: data.interactions,
          account: user.account_id,

          // ✅ NOVOS CAMPOS
          simple_agent: data.simple_agent || false, // Agentes avançados são marcados como false
          is_published: data.is_published || false, // Manter status de publicação
          model: data.model || "gpt-4.1-mini",
          is_active: data.is_active || 0,
        };

        // ✅ NOVO: Incluir uploadedImages no rawAgentData
        const agentDataWithImages = prepareAgentData(
          rawAgentData,
          uploadedImages
        );

        console.log(
          "🔍 Dados para primeira requisição (JSON):",
          agentDataWithImages
        );
        console.log(
          "🔍 Arquivo de imagem para segunda requisição:",
          agentImageFile
        );

        let response;
        let createdAgentId;

        if (isEditing && agentId) {
          // ✅ ATUALIZAR AGENTE EXISTENTE
          console.log("🔄 Atualizando agente:", agentId);

          // Para edição, se há imagem nova, ainda usar o método atual
          createdAgentId = agentId;
          response = await updateAgent(agentId, agentDataWithImages);

          Swal.fire({
            icon: "success",
            title: "Agente atualizado com sucesso!",
            text: "As alterações foram salvas.",
            timer: 3000,
          });
        } else {
          // ✅ CRIAR NOVO AGENTE - PRIMEIRA REQUISIÇÃO (JSON)
          console.log("🆕 Criando novo agente - Primeira requisição (JSON)");

          // Adicionar is_active para criação
          agentDataWithImages["is_active"] = 0;

          // Primeira requisição: criar agente sem imagem
          response = await createAgent(agentDataWithImages);

          console.log("🔍 Resposta da primeira requisição:", response.data);

          // Extrair ID do agente criado
          createdAgentId = response.data.id;

          if (!createdAgentId) {
            throw new Error("ID do agente não retornado pela API");
          }

          Swal.fire({
            icon: "success",
            title: "Agente criado com sucesso!",
            text: "Seu agente de IA foi configurado e está pronto para uso.",
            timer: 3000,
          });
        }

        // ✅ SEGUNDA REQUISIÇÃO: Upload da imagem se existir
        if (agentImageFile) {
          console.log(
            "🔄 Fazendo upload da imagem do agente - Segunda requisição"
          );

          try {
            // Criar FormData apenas com a imagem
            const imageFormData = new FormData();
            imageFormData.append("agent_image", agentImageFile);

            // Fazer update do agente com a imagem
            const imageResponse = await updateAgent(
              createdAgentId,
              imageFormData
            );
            console.log("🔍 Resposta do upload da imagem:", imageResponse.data);
          } catch (imageError) {
            console.error("Erro ao fazer upload da imagem:", imageError);
            // Não falhar a criação do agente por erro na imagem
            Swal.fire({
              icon: "warning",
              title: "Agente criado, mas erro no upload da imagem",
              text: "O agente foi criado com sucesso, mas houve um erro ao fazer upload da imagem. Você pode editá-lo posteriormente para adicionar a imagem.",
              timer: 5000,
            });
          }
        }

        // Limpar rascunho após sucesso (apenas quando criando)
        if (!isEditing && savedDraft?.id) {
          try {
            await clearDraft(savedDraft.id);
            setSavedDraft(null);
          } catch (error) {
            console.error("Erro ao limpar rascunho:", error);
          }
        }

        setNotify({
          type: "success",
          title: "success",
          message: isEditing
            ? "Agent editado com sucesso!"
            : "Agente criado com sucesso!",
        });

        setTimeout(() => {
          window.location.href = "/agents";
        }, 2500);
      } catch (error) {
        console.error(
          `Erro ao ${isEditing ? "atualizar" : "criar"} agente:`,
          error
        );

        // Tratamento de erro mais específico
        let errorMessage = `Ocorreu um erro ao ${
          isEditing ? "atualizar" : "salvar"
        } o agente. Tente novamente.`;

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire({
          icon: "error",
          title: `Erro ao ${isEditing ? "atualizar" : "salvar"}`,
          text: errorMessage,
        });

        // Notificar via setNotify se disponível
        if (setNotify) {
          setNotify({
            type: "error",
            title: `Erro ao ${isEditing ? "atualizar" : "salvar"}`,
            message: errorMessage,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    // Step titles and icons (atualizado para 7 steps)
    const steps = [
      { title: "Identidade", icon: IconUser },
      { title: "Linguagem", icon: IconPalette },
      { title: "Sobre o Negócio", icon: IconBuilding },
      { title: "Como Conversar", icon: IconMessageCircle },
      { title: "Funções", icon: IconFunction },
      { title: "Interações", icon: IconHeart },
      { title: "Final", icon: IconFileText },
    ];

    // Flow step options updated with better labels for basic mode (otimizado)
    const getFlowStepsForMode = React.useMemo(
      () => (mode) => {
        if (mode === "basic") {
          return [
            {
              key: "greeting",
              label: "Cumprimentar o Cliente",
              icon: "👋",
              description:
                "Como o agente vai cumprimentar cada pessoa que chegar",
            },
            {
              key: "needIdentification",
              label: "Descobrir o que a Pessoa Quer",
              icon: "🤔",
              description: "Como perguntar o que o cliente está procurando",
            },
            {
              key: "presentation",
              label: "Explicar Como Você Pode Ajudar",
              icon: "💡",
              description: "Como apresentar seus produtos ou serviços",
            },
            {
              key: "closing",
              label: "Finalizar o Atendimento",
              icon: "🎯",
              description: "Como encerrar a conversa e definir próximos passos",
            },
          ];
        } else {
          return [
            {
              key: "greeting",
              label: "Saudação",
              icon: "🎯",
              description: "Primeira mensagem do agente",
            },
            {
              key: "presentation",
              label: "Apresentação",
              icon: "🏢",
              description: "Como o agente apresenta o seu negócio",
            },
            {
              key: "needIdentification",
              label: "Identificação da Necessidade",
              icon: "🔍",
              description: "Como descobrir o que o cliente precisa",
            },
            {
              key: "direction",
              label: "Direcionamento",
              icon: "🎯",
              description: "Como direcionar o cliente para a solução",
            },
            {
              key: "confirmation",
              label: "Confirmação",
              icon: "✅",
              description: "Como confirmar detalhes e informações",
            },
            {
              key: "upsell",
              label: "Upsell (Opcional)",
              icon: "💡",
              description: "Como oferecer produtos ou serviços adicionais",
            },
            {
              key: "availability",
              label: "Disponibilidade",
              icon: "📅",
              description: "Como verificar e apresentar disponibilidade",
            },
            {
              key: "dataCollection",
              label: "Coleta de Dados",
              icon: "📋",
              description: "Como coletar informações necessárias",
            },
            {
              key: "closing",
              label: "Finalização",
              icon: "🎉",
              description: "Como finalizar o atendimento",
            },
          ];
        }
      },
      []
    );

    // Função para renderizar o conteúdo da etapa atual
    const renderStepContent = () => {
      switch (currentStep) {
        case 1:
          return (
            <StepIdentity
              register={register}
              watch={watch}
              setValue={setValue}
              errors={errors}
              control={control}
              isEditing={isEditing}
            />
          );
        case 2:
          return (
            <StepLanguage
              register={register}
              watch={watch}
              setValue={setValue}
              errors={errors}
              control={control}
            />
          );
        case 3:
          return (
            <StepBusiness
              register={register}
              watch={watch}
              setValue={setValue}
              errors={errors}
              control={control}
            />
          );
        case 4:
          return (
            <StepConversation
              register={register}
              watch={watch}
              setValue={setValue}
              flowMode={flowMode}
              setFlowMode={setFlowMode}
              handleAIFill={handleAIFill}
              ImageSelect={ImageSelect}
              CONVERSATION_TEMPLATES={CONVERSATION_TEMPLATES}
              applyTemplate={applyTemplate}
              NEXT_STEP_OPIONS={NEXT_STEP_OPIONS}
              addSpecialFlow={addSpecialFlow}
              removeSpecialFlow={removeSpecialFlow}
              moveFlow={moveFlow}
              specialFlows={memoizedSpecialFlows}
              getFlowStepsForMode={getFlowStepsForMode}
            />
          );
        case 5:
          return (
            <StepFunctions
              register={register}
              watch={watch}
              setValue={setValue}
              unregister={unregister}
              control={control}
              handleAIFill={handleAIFill}
              onGetSchemas={setGetGeneratedSchemas}
            />
          );
        case 6:
          return (
            <StepInteractions
              register={register}
              watch={watch}
              setValue={setValue}
              control={control}
              handleAIFill={handleAIFill}
              MICRO_INTERACTIONS={MICRO_INTERACTIONS}
            />
          );
        case 7:
          return (
            <StepFinalPrompt
              generatePromptPreview={null}
              copyPrompt={null}
              exportPrompt={null}
              getValues={getValues}
              AREA_ACTIVITY_OPTIONS={AREA_ACTIVITY_OPTIONS}
              uploadedImages={uploadedImages}
              isEditing={isEditing}
            />
          );
        default:
          return null;
      }
    };

    // Loading state during data loading
    if (dataLoading) {
      return (
        <Container className="">
          <div className="text-center py-5">
            <Spinner className="mb-3" />
            <h5>Carregando dados do agente...</h5>
            <p className="text-muted">
              Aguarde enquanto carregamos os dados para edição.
            </p>
          </div>
        </Container>
      );
    }

    return (
      <DndProvider backend={HTML5Backend}>
        <>
          {/* Mobile Header */}
          <div className="agent-wizard-header d-md-none">
            <div className="agent-wizard-header-content">
              {onBack && (
                <Button className="agent-wizard-back-btn" onClick={onBack}>
                  <IconChevronLeft size={16} />
                  Voltar
                </Button>
              )}
              <h1 className="agent-wizard-title">
                {isEditing
                  ? "Editar Agente"
                  : isUpgradeFromBasic
                  ? "Agente Avançado"
                  : "Criar Agente"}
              </h1>
              {!isEditing && (
                <Button
                  className="agent-wizard-save-draft"
                  onClick={handleSaveDraft}
                  disabled={draftLoading}
                >
                  {draftLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <IconDeviceFloppy size={16} />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Progress */}
          <div className="agent-wizard-progress-container d-md-none">
            <div className="agent-wizard-progress-bar">
              <div
                className="agent-wizard-progress-fill"
                style={{ width: `${(currentStep / 7) * 100}%` }}
              ></div>
            </div>
            <div className="agent-wizard-step-info">
              <span className="agent-wizard-step-title">
                {steps[currentStep - 1]?.title}
              </span>
              <span>Etapa {currentStep} de 7</span>
            </div>
          </div>

          {/* Main Container */}
          <div className="card no-hover pb-4">
            <div className="agent-wizard-mobile-container d-md-none">
              <div className="agent-wizard-content">
                {/* Mobile Alerts */}
                {isEditing && (
                  <Alert variant="info" className="d-md-none">
                    <strong>Editando agente</strong>
                    <br />
                    Todas as alterações serão salvas ao finalizar.
                  </Alert>
                )}

                {isUpgradeFromBasic && (
                  <Alert variant="success" className="d-md-none">
                    <strong>Upgrade para Avançado!</strong>
                    <br />
                    Configure fluxos personalizados e funções avançadas.
                  </Alert>
                )}

                {/* Form Content */}
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div className="agent-wizard-form-content">
                    {/* Renderizar conteúdo da etapa atual */}
                    {renderStepContent()}
                  </div>
                </Form>
              </div>
            </div>

            {/* Desktop Layout (unchanged) */}
            <Row className="d-none d-md-flex">
              <Col lg={12} sm={12} className="mx-auto">
                <div className="p-4">
                  <Row>
                    <Col lg={12}>
                      {/* Header */}
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div className="d-flex align-items-center">
                          {onBack && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="me-3"
                              onClick={onBack}
                            >
                              <IconChevronLeft className="me-1" size={16} />
                              Voltar
                            </Button>
                          )}
                          <h3 className="mb-0">
                            <IconRocket className="me-2" />
                            {isEditing
                              ? "Editar Agente"
                              : isUpgradeFromBasic
                              ? "Criar Agente"
                              : "Criar Agente"}
                            {isEditing && (
                              <Badge bg="warning" className="ms-2">
                                Modo Edição
                              </Badge>
                            )}
                            {isUpgradeFromBasic && (
                              <Badge bg="info" className="ms-2">
                                Upgrade do Básico
                              </Badge>
                            )}
                          </h3>
                        </div>
                        <div>
                          {!isEditing && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleSaveDraft}
                              disabled={draftLoading}
                            >
                              {draftLoading ? (
                                <Spinner size="sm" className="me-1" />
                              ) : (
                                <IconDeviceFloppy className="me-1" size={16} />
                              )}
                              Salvar Rascunho
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Alert for edit mode */}
                      {isEditing && (
                        <Alert variant="info" className="mb-4 p-2">
                          <strong>Modo de Edição:</strong> Você está editando um
                          agente existente. Todas as alterações serão salvas ao
                          finalizar.
                        </Alert>
                      )}

                      {/* Alert for upgrade mode */}
                      {isUpgradeFromBasic && (
                        <Alert variant="success" className="mb-4 p-3">
                          <div className="d-flex align-items-start">
                            <IconRocket
                              className="me-2 mt-1 text-success"
                              size={20}
                            />
                            <div>
                              <strong>Upgrade para Avançado!</strong>
                              <p className="mb-0 mt-1">
                                Suas informações básicas foram importadas. Agora
                                você pode configurar fluxos personalizados,
                                funções avançadas e muito mais!
                              </p>
                            </div>
                          </div>
                        </Alert>
                      )}

                      {/* Progress Bar */}
                      <ProgressBar
                        now={(currentStep / 7) * 100}
                        className="mb-4"
                        style={{ height: "8px" }}
                      />

                      {/* Steps Navigation */}
                      <div
                        className="agent-wizard-desktop-steps d-flex justify-content-center mb-4"
                        style={{ flexWrap: "wrap", gap: "10px" }}
                      >
                        {steps.map((step, index) => {
                          const StepIcon = step.icon;
                          const stepNumber = index + 1;
                          const isActive = currentStep === stepNumber;
                          const isCompleted = currentStep > stepNumber;

                          return (
                            <div
                              key={stepNumber}
                              className={`d-flex flex-column align-items-center ${
                                isActive
                                  ? "text-primary"
                                  : isCompleted
                                  ? "text-success"
                                  : "text-muted"
                              }`}
                              style={{ cursor: "pointer", minWidth: "80px" }}
                              onClick={() =>
                                stepNumber < currentStep &&
                                setCurrentStep(stepNumber)
                              }
                            >
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                                  isActive
                                    ? "bg-primary text-white"
                                    : isCompleted
                                    ? "bg-success text-white"
                                    : "bg-light text-muted"
                                }`}
                                style={{ width: "40px", height: "40px" }}
                              >
                                <StepIcon size={20} />
                              </div>
                              <small className="text-center">
                                {step.title}
                              </small>
                            </div>
                          );
                        })}
                      </div>

                      {/* Form Content */}
                      <Form onSubmit={handleSubmit(onSubmit)}>
                        <div className="">
                          {/* Renderizar conteúdo da etapa atual */}
                          {renderStepContent()}

                          {/* Navigation Footer */}
                          <div className="d-flex justify-content-between align-items-center">
                            <Button
                              variant="outline-secondary"
                              onClick={prevStep}
                              disabled={currentStep === 1}
                            >
                              <IconChevronLeft className="me-1" size={16} />
                              Anterior
                            </Button>

                            <span className="text-muted">
                              Etapa {currentStep} de 7
                            </span>

                            {currentStep < 7 ? (
                              <Button variant="primary" onClick={nextStep}>
                                Próximo
                                <IconChevronRight className="ms-1" size={16} />
                              </Button>
                            ) : (
                              <Button
                                variant="success"
                                type="submit"
                                disabled={loading}
                              >
                                {loading ? (
                                  <Spinner size="sm" className="me-1" />
                                ) : (
                                  <IconDeviceFloppy
                                    className="me-1"
                                    size={16}
                                  />
                                )}
                                {isEditing
                                  ? "Salvar Alterações"
                                  : "Criar Agente"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </Form>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>

          {/* Mobile Bottom Navigation */}
          <div className="agent-wizard-bottom-nav d-md-none">
            <div className="agent-wizard-nav-content">
              <Button
                className="agent-wizard-prev-btn"
                onClick={prevStep}
                disabled={currentStep === 1}
              >
                <IconChevronLeft size={18} />
                Anterior
              </Button>

              {currentStep < 7 ? (
                <Button className="agent-wizard-next-btn" onClick={nextStep}>
                  Próximo
                  <IconChevronRight size={18} />
                </Button>
              ) : (
                <Button
                  className="agent-wizard-submit-btn"
                  type="submit"
                  disabled={loading}
                  onClick={handleSubmit(onSubmit)}
                >
                  {loading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <IconDeviceFloppy size={18} />
                  )}
                  {isEditing ? "Salvar" : "Criar Agente"}
                </Button>
              )}
            </div>
          </div>

          {/* AI Fill Modal */}
          <AIFillModal
            show={showAIModal}
            onHide={() => {
              setShowAIModal(false);
              setAiStepInfo(null);
            }}
            onGenerate={handleAIGenerate}
            stepInfo={aiStepInfo}
            loading={aiLoading}
          />
        </>
      </DndProvider>
    );
  }
);

AgentWizard.displayName = "AgentWizard";

export default AgentWizard;
